const ratingColors = {
  unknown: {
    primary: '#D7D7DC',
    text: '#403F40',
  },
  low: {
    primary: '#FFC9D2',
    text: '#80000F',
  },
  medium: {
    primary: '#FFD274',
    text: '#792102',
  },
  high: {
    primary: '#C4E0B1',
    text: '#0D4A15',
  },
  elite: {
    primary: '#75BD74',
    text: '#07220C',
  },
}

export const productColors = {
  // #DESIGN_2023
  primary: '#023D67',
  secondary: '#15B1D7',
  highlight: '#E9560D',
  primaryHover: '#0C2D49',
  primaryDisabled: '#173C64',
  primaryActive: `#022F4F`,
  cgBlue: '#03749C',
  skyBlue: '#5CD4EF',
  brownCherry: '#A82F02',
  pumpkin: '#F88011',
  brightYellow: '#FFAB2E',
  vividGamboge: '#FF9900',
  icon: '#9BA9B0',
  gray: {
    50: '#F2F3F7',
    100: '#E5E5E9',
    200: '#D7D7DC',
    300: '#CACACE',
    400: '#BDBCC0',
    500: '#B0AFB3',
    600: '#A3A1A5',
    700: '#959497',
    800: '#888689',
    900: '#7B797C',
  },
  blue: {
    50: '#E2EFF4',
    100: '#B8D7E6',
    200: '#8EBFD6',
    300: '#68A6C5',
    400: '#4D95BC',
    500: '#3385B3',
    600: '#2878A7',
    700: '#1B6897',
    800: '#115886',
    900: '#023D67',
  },
  cyan: {
    50: '#E2F7FF',
    100: '#B5ECFD',
    200: '#85E0FB',
    300: '#53D3F6',
    400: '#2FCAF1',
    500: '#1DC0EB',
    600: '#15B1D7',
    700: '#039BBC',
    800: '#0088A2',
    900: '#006575',
  },
  border: {
    solid: '#DADFF1',
    subtle: '#E2E8F0',
    dashed: '#9BA9B0',
  },
  text: {
    light: {
      header: '#072E45',
      bodySolid: '#E2EFF4',
      bodySubtle: '#8EBFD6',
      link: '#5CD4EF',
      linkInBody: '#03749C',
      buttonLink: '#023D67',
    },
    dark: {
      black: '#001C2D',
      error: '#85000F',
      header: '#072E45',
      bodySolid: '#3E4661',
      bodySubtle: '#516B7B',
      link: '#03749C',
      buttonLink: '#023D67',
      titleSubtle: '#173A4FB2',
      placeholder: '#959DBB',
      ranking: '#403F40',
    },
    state: {
      info: '#072E45',
      success: '#105E1D',
      warning: '#A22C02',
      error: '#BC0017',
    },
  },
  // state and tags have same colors
  state: {
    subtle: {
      info: '#E2EFF4',
      success: '#C4E0B1',
      warning: '#FFD274',
      error: '#FFC9D2',
    },
    solid: {
      info: '#023D67',
      success: '#105E1D',
      warning: '#E9560D',
      error: '#CA0024',
    },
  },
  button: {
    primary: {
      500: '#023D67',
      600: '#0C2D49',
    },
  },
  table: {
    hover: '#F1FBFF',
  },
  rating: ratingColors,
  progress: {
    500: '#15B1D7',
    200: '#F2F3F7',
  },
}

export const baseColors = {
  orange: {
    50: '#FDF3E7',
    100: '#EDECE0',
    400: '#FB8C00',
    500: '#E9560D',
    600: '#FB8C00',
    700: '#EF860A',
  },
  red: {
    100: '#F3DADA',
    450: '#E53E3E',
    500: '#F41A28',
  },
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    400: '#60A5FA',
    500: '#114F73',
    600: '#0E405D',
    700: '#0A3047',
    900: '#011724',
  },
  royalBlue: {
    50: '#F6F9FE',
    100: '#EEF3FD', // secondary
    200: '#D3E0FB',
    300: '#B3C8F8',
    400: '#719AF2',
    500: '#2563EB', // primary
    501: 'rgba(37, 99, 235, 0.1)',
    502: 'rgba(37, 99, 235, 0.2)',
    506: 'rgba(37, 99, 235, 0.6)',
    507: 'rgba(37, 99, 235, 0.7)',
    600: '#1248C0', // hover
  },
  purple: {
    500: '#6A2098',
  },
  gray: {
    50: '#F2F3F7',
    100: '#FAFAFA',
    125: '#F5F6F6',
    150: '#F4F4F4',
    200: '#F9F9FA',
    300: '#E9EBED',
    400: '#BDBDBD',
    425: '#B8B8B8',
    450: '#ABAEB4',
    500: '#E0E0E0',
    525: '#94A3B8',
    550: '#9E9E9E',
    600: '#546E7A',
    700: '#455A64',
    750: '#C9D1DB',
    900: '#212121',
  },
  blueGray: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    250: '#D4DAE3',
    300: '#CBD5E1',
    400: '#94A3B8',
    401: '#F2F5F7',
    403: 'rgba(148, 163, 184, 0.3)',
    404: 'rgba(148, 163, 184, 0.4)',
    405: 'rgba(148, 163, 184, 0.5);',
    406: 'rgba(148, 163, 184, 0.6);',
    450: '#95A3B8',
    500: '#64748B',
    600: '#475569',
    650: '#344156',
    700: '#072E45',
    701: 'rgba(7, 46, 69, 0.1)',
    702: 'rgba(7, 46, 69, 0.2)',
    705: 'rgba(7, 46, 69, 0.5)',
    706: 'rgba(7, 46, 69, 0.6)',
    707: 'rgba(7, 46, 69, 0.7)',
    800: '#1E293B',
    900: '#0F172A',
    907: 'rgba(15, 23, 42, 0.7)',
  },
  graphiqlGray: {
    100: '#F6F7F8',
    200: '#EEEEEE',
    300: '#E0E0E0',
  },
  green: {
    50: '#ECFDF5',
    500: '#10B981',
    550: '#16A34A',
    600: '#059669',
  },
  orangeAlpha: {
    400: 'rgba(251, 140, 0, 0.1)',
    800: '#37474F',
    900: '#263238',
    950: '#212121',
  },
  teal: {
    50: '#E0F2F1',
    600: '#00897B',
  },
  progressBlue: {
    500: '#2563EB',
  },
  progressGreen: {
    500: '#059669',
  },
  progressOrange: {
    500: '#EA580C',
  },
  tabBlueGray: {
    100: '#E2E8F0',
    700: '#072E45',
  },
  metabaseGreen: {
    500: '#84BB4C',
  },
  metabaseRed: {
    500: '#ED6E6E',
  },
  metabaseBlue: {
    500: '#519EE2',
  },
  metabaseTeal: {
    500: '#114F73',
  },
  state: {
    subtle: {
      info: '#E2F7FF',
      warning: '#FFD597',
    },
    solid: {
      info: '#023D67',
      warning: '#E9560D',
    },
  },
  text: {
    state: {
      info: '#072E45',
      warning: '#A22C02',
    },
  },
}

export const colors = {
  product: productColors,
  ...baseColors,
}
